import { useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import { EVALUATION_ASPECTS } from '@/lib/evaluationAspects'

export interface CompanyAspect {
  key: string
  label: string
  description?: string
  enabled: boolean
  custom: boolean
}

export interface CompanyAspectsDoc {
  _id?: string
  companyUserId: string
  companyName?: string
  aspects: CompanyAspect[]
  creator?: string
  createdAt?: string
  updatedAt?: string
}

/** Aspectos base de la plataforma convertidos a la estructura configurable (todos habilitados por defecto). */
export function defaultCompanyAspects(): CompanyAspect[] {
  return EVALUATION_ASPECTS.map((a) => ({
    key: a.key,
    label: a.label,
    description: a.description,
    enabled: true,
    custom: false,
  }))
}

/**
 * Combina la configuración guardada con los aspectos base actuales:
 * - Conserva el estado (enabled) de los aspectos base ya configurados.
 * - Agrega aspectos base nuevos que aún no estuvieran en el documento.
 * - Mantiene los aspectos personalizados (custom) creados por la empresa.
 */
export function mergeWithDefaults(saved: CompanyAspect[] | undefined): CompanyAspect[] {
  const base = defaultCompanyAspects()
  if (!saved || saved.length === 0) return base
  const savedMap = new Map(saved.map((a) => [a.key, a]))
  const merged: CompanyAspect[] = base.map((b) => {
    const found = savedMap.get(b.key)
    return found ? { ...b, enabled: found.enabled } : b
  })
  // Agregar los personalizados que no son base
  const baseKeys = new Set(base.map((b) => b.key))
  for (const a of saved) {
    if (!baseKeys.has(a.key)) merged.push({ ...a, custom: true })
  }
  return merged
}

export function useCompanyAspects() {
  const [loading, setLoading] = useState(false)

  /** Obtiene los aspectos efectivos (habilitados) de una empresa. Si no tiene configuración, usa los base. */
  const fetchEffectiveAspects = useCallback(async (companyUserId: string): Promise<CompanyAspect[]> => {
    try {
      const { list } = await lumi.entities.company_eval_aspects.list({
        filter: { companyUserId },
        limit: 1,
      })
      const doc = (list as CompanyAspectsDoc[])[0]
      const merged = mergeWithDefaults(doc?.aspects)
      return merged.filter((a) => a.enabled)
    } catch (e) {
      console.error('Error al cargar aspectos efectivos:', e)
      return defaultCompanyAspects()
    }
  }, [])

  /** Obtiene el documento completo (para edición). Devuelve la lista combinada con base. */
  const fetchConfig = useCallback(async (companyUserId: string): Promise<{ doc: CompanyAspectsDoc | null; aspects: CompanyAspect[] }> => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.company_eval_aspects.list({
        filter: { companyUserId },
        limit: 1,
      })
      const doc = (list as CompanyAspectsDoc[])[0] ?? null
      return { doc, aspects: mergeWithDefaults(doc?.aspects) }
    } catch (e) {
      console.error('Error al cargar configuración de aspectos:', e)
      return { doc: null, aspects: defaultCompanyAspects() }
    } finally {
      setLoading(false)
    }
  }, [])

  /** Guarda (crea o actualiza) la configuración de aspectos de una empresa. */
  const saveConfig = useCallback(async (params: {
    docId?: string
    companyUserId: string
    companyName?: string
    aspects: CompanyAspect[]
    creator: string
  }) => {
    const now = new Date().toISOString()
    const { docId, companyUserId, companyName, aspects, creator } = params
    if (docId) {
      return lumi.entities.company_eval_aspects.update(docId, {
        aspects,
        companyName,
        updatedAt: now,
      })
    }
    return lumi.entities.company_eval_aspects.create({
      companyUserId,
      companyName: companyName ?? '',
      aspects,
      creator,
      createdAt: now,
      updatedAt: now,
    })
  }, [])

  /** Carga TODAS las configuraciones (para vista admin). */
  const fetchAll = useCallback(async (): Promise<CompanyAspectsDoc[]> => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.company_eval_aspects.list({ sort: { updatedAt: -1 } })
      return list as CompanyAspectsDoc[]
    } catch (e) {
      console.error('Error al cargar todas las configuraciones de aspectos:', e)
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  return { loading, fetchEffectiveAspects, fetchConfig, saveConfig, fetchAll }
}
